// ── Geração de links Google Meet via webhook n8n ──────────────────
// Núcleo puro do domínio Agenda: NÃO usa Supabase. É apenas uma chamada
// HTTP externa (n8n) encapsulada. Sem estado, sem dependência de porta.

const MEET_TIMEOUT_MS = 8000;

const buildBasicAuth = (user: string | undefined, pass: string | undefined): string | null => {
  if (!user || !pass) return null;
  const raw = `${user}:${pass}`;
  if (typeof btoa === 'function') return btoa(raw);
  return null;
};

export interface GenerateMeetLinkParams {
  meetingId?: string | null;
  closerId?: string | null;
  scheduledAt?: string | null;
  leadName?: string | null;
  funnelName?: string | null;
}

export const generateMeetLink = async ({
  meetingId,
  closerId,
  scheduledAt,
  leadName,
  funnelName,
}: GenerateMeetLinkParams): Promise<string | null> => {
  const baseUrl = process.env.REACT_APP_N8N_MEET_WEBHOOK_URL;
  const user = process.env.REACT_APP_N8N_MEET_WEBHOOK_USER;
  const pass = process.env.REACT_APP_N8N_MEET_WEBHOOK_PASS;
  const auth = buildBasicAuth(user, pass);

  if (!baseUrl || !closerId || !scheduledAt || !auth) return null;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), MEET_TIMEOUT_MS);

  try {
    const url = `${baseUrl.replace(/\/$/, '')}/${closerId}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${auth}`,
      },
      body: JSON.stringify({
        meeting_id: meetingId || null,
        horario_reuniao: scheduledAt,
        dia_reuniao: scheduledAt,
        closer_id: closerId,
        nome_lead: leadName || '',
        nome_funil: funnelName || '',
      }),
      signal: controller.signal,
    });

    if (!response.ok) return null;
    const data: unknown = await response.json().catch(() => null);
    const payload = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null | undefined;
    const raw = payload?.link ?? payload?.['Link meet'] ?? payload?.link_meet ?? payload?.meetLink ?? null;
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
};

// ── Disponibilidade unificada do closer (Google Calendar via n8n) ─────
// Agrega os eventos do closer em TODOS os workspaces (o webhook resolve isso
// por fora). Devolve intervalos ocupados para bloquear slots na agenda.
export interface BusyInterval { start: Date; end: Date; }

interface ExternalCalEvent {
  status?: string;
  start?: { dateTime?: string };
  end?: { dateTime?: string };
}

export const getCloserExternalBusy = async (
  closerId: string | null | undefined,
): Promise<BusyInterval[]> => {
  const baseUrl = process.env.REACT_APP_N8N_DISPONIBILIDADE_WEBHOOK_URL;
  if (!baseUrl || !closerId) return [];

  // Mesmo base/UUID do webhook de Meet → reusa o basic auth (se o node exigir).
  const auth = buildBasicAuth(
    process.env.REACT_APP_N8N_MEET_WEBHOOK_USER,
    process.env.REACT_APP_N8N_MEET_WEBHOOK_PASS,
  );

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), MEET_TIMEOUT_MS);

  try {
    const url = `${baseUrl.replace(/\/$/, '')}/${closerId}`;
    const response = await fetch(url, {
      signal: controller.signal,
      headers: auth ? { Authorization: `Basic ${auth}` } : undefined,
    });
    if (!response.ok) return [];

    const data: unknown = await response.json().catch(() => null);
    if (!Array.isArray(data)) return [];

    return (data as ExternalCalEvent[])
      .filter(e => e?.status !== 'cancelled' && e?.start?.dateTime && e?.end?.dateTime)
      .map(e => ({
        // dateTime carrega o offset (-03:00) → new Date resolve o instante certo.
        start: new Date(e.start!.dateTime!),
        end: new Date(e.end!.dateTime!),
      }));
  } catch {
    return [];
  } finally {
    clearTimeout(timeout);
  }
};
